import { useEffect, useLayoutEffect, useMemo, useState } from 'react';

import { EntityRefUtil, GetSchedulesRequestDTO, GetSchedulesResponseDTO, MODULE, RecordState } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RowSelectionState } from '@tanstack/react-table';
import { useDebounce } from '@uidotdev/usehooks';
import { useLocation } from 'react-router-dom';
import { z } from 'zod';

import { ArchiveConfirmationDialog } from '@/components/common/archive-confirmation-dialog';
import { RestoreConfirmationDialog } from '@/components/common/restore-confirmation-dialog';
import { ShareDialog } from '@/components/common/share-dialog';
import { TypeDeleteConfirmationDialog } from '@/components/common/type-delete-confirmation-dialog';
import { FeedbackPrompt } from '@/components/feedback/feedback-prompt';
import ArchiveIcon from '@/components/icons/archive';
import ClockIcon from '@/components/icons/clock';
import CloneIcon from '@/components/icons/clone';
import DeleteIcon from '@/components/icons/delete';
import FolderOpenIcon from '@/components/icons/folder-open';
import GridIcon from '@/components/icons/grid';
import LinkViewIcon from '@/components/icons/link-view';
import PencilIcon from '@/components/icons/pencil';
import PlusIcon from '@/components/icons/plus';
import RegisterIcon from '@/components/icons/register';
import UndoIcon from '@/components/icons/undo';
import { TablePageGridView } from '@/components/table-page/table-page-grid-view';
import { TablePageHeader, TablePageHeaderActionButton } from '@/components/table-page/table-page-header';
import TablePageSearchBar from '@/components/table-page/table-page-search-bar';
import { TablePageSelectedCountIndicator } from '@/components/table-page/table-page-selected-count-indicator';
import { TablePageTitle } from '@/components/table-page/table-page-title';
import { useTopbarStore } from '@/components/topbar/topbar-store';
import { ButtonStateToggle } from '@/components/ui/button-state-toggle';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { useToast } from '@/components/ui/toaster';
import { env } from '@/env';
import { useFeedbackPrompt } from '@/hooks/use-feedback-prompt';
import { useInfiniteScrollTrigger } from '@/hooks/use-infinite-scroll-trigger';
import { usePageConfig } from '@/hooks/use-page-config';
import { usePermissions } from '@/hooks/use-permissions';
import { useResetRowSelectionOnChange } from '@/hooks/use-reset-row-selection-on-change';
import ShareIcon from '@/legacy/components/icons/share';
import { getSchedulesInfiniteQueryOptions, scheduleKeys } from '@/queries/schedule';
import { ApiService } from '@/services/api-v2';
import { useModulesStore } from '@/stores/modules.store';
import { useUserStore } from '@/stores/user.store';
import { useWorkspaceNavigate } from '@/utils/routes';

import { NoSchedulesCreatedDisplay, NoSchedulesFoundFromFiltersDisplay, NoSchedulesFoundFromSearchDisplay, SchedulesTable } from './table';
import { createSchedulesTableColumns, FILTER_FIELD_MAP } from './table-meta';
import { TopbarActions } from './topbar-actions';

const SchedulesLocationStateSchema = z.object({
  showFeedback: z.boolean().optional(),
  scheduleId: z.string().optional(),
  scheduleName: z.string().optional(),
});

const SchedulesPage = () => {
  const queryClient = useQueryClient();
  const navigate = useWorkspaceNavigate();
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const toast = useToast();
  const { setModuleFilters, syncModuleColumns } = useModulesStore();
  const { canManageSchedules } = usePermissions();

  const { org } = useUserStore();

  const {
    config: { columnOrder, grouping, sorting, filters, view, columnVisibility },
    loading: isLoadingPageConfig,
    updatePageConfig,
  } = usePageConfig(MODULE.Schedules, {
    filterFieldMap: FILTER_FIELD_MAP,
    columns: createSchedulesTableColumns({ canManageSchedules }),
  });

  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [openArchiveConfirmation, setOpenArchiveConfirmation] = useState(false);
  const [openRestoreConfirmation, setOpenRestoreConfirmation] = useState(false);
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const [scheduleState, setScheduleState] = useState<RecordState>(RecordState.Active);
  const [shareUrl, setShareUrl] = useState<string | null>(null);

  const location = useLocation();
  const { trigger: triggerFeedback, promptProps: feedbackPromptProps } = useFeedbackPrompt('schedule-editor');
  const locationState = SchedulesLocationStateSchema.safeParse(location.state).data;
  const scheduleId = locationState?.scheduleId;
  const scheduleName = locationState?.scheduleName;

  useLayoutEffect(() => {
    useTopbarStore.getState().setActions(<TopbarActions />);

    return () => {
      useTopbarStore.getState().setActions(<></>);
    };
  }, []);

  useEffect(() => {
    if (locationState?.showFeedback) {
      window.history.replaceState({}, '');
      triggerFeedback({ scheduleId, scheduleName });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once on mount; locationState + triggerFeedback are stable references and the modal must not re-fire on re-renders
  }, []);

  const {
    data: schedulesData,
    fetchNextPage: fetchNextSchedulesPage,
    hasNextPage: hasNextSchedulesPage,
    isFetching: isFetchingSchedules,
    isFetchingNextPage: isFetchingNextSchedulesPage,
    error: schedulesError,
  } = useInfiniteQuery(
    getSchedulesInfiniteQueryOptions({
      filters: {
        state: scheduleState,
        search: debouncedSearch,
        fields: Object.fromEntries(Object.entries(FILTER_FIELD_MAP).map(([id, key]) => [key, (filters || []).find((filter) => filter.id === id)?.value])),
      },
      orderBy: sorting.key
        ? { field: sorting.key as NonNullable<GetSchedulesRequestDTO['orderBy']>['field'], direction: sorting.direction as 'asc' | 'desc' }
        : undefined,
      enabled: !isLoadingPageConfig,
    }),
  );

  const getSelectedSchedules = useMemo(() => {
    return (incomingData: typeof schedulesData) => {
      if (!incomingData?.pages) return [];
      const ids = Object.keys(selectedRows);
      const schedules = incomingData.pages.flatMap((page) => page.data);
      return ids.filter((id) => !isNaN(parseInt(id))).map((id) => schedules[parseInt(id)]);
    };
  }, [selectedRows]);

  const { mutate: deleteSchedules, isPending: isDeletingSchedules } = useMutation({
    mutationFn: async (ids: string[]) => await ApiService.BulkUpdateScheduleState({ scheduleIds: ids, state: RecordState.Deleted }),
    onSuccess: () => {
      toast.success('Schedules deleted successfully');
      setOpenDeleteConfirmation(false);
      setSelectedRows({});

      queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleDeleteSchedules = () => {
    if (schedulesData?.pages) {
      const toDelete = getSelectedSchedules(schedulesData);
      deleteSchedules(toDelete.map((schedule) => schedule.id));
    }
  };

  const { mutate: archiveSchedules, isPending: isArchivingSchedules } = useMutation({
    mutationFn: async (ids: string[]) => await ApiService.BulkUpdateScheduleState({ scheduleIds: ids, state: RecordState.Archived }),
    onSuccess: () => {
      toast.success('Schedules archived successfully');
      setOpenArchiveConfirmation(false);
      setSelectedRows({});

      queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const { mutate: restoreSchedules, isPending: isRestoringSchedules } = useMutation({
    mutationFn: async (ids: string[]) => await ApiService.BulkUpdateScheduleState({ scheduleIds: ids, state: RecordState.Active }),
    onSuccess: () => {
      toast.success('Schedules restored successfully');
      setOpenRestoreConfirmation(false);
      setSelectedRows({});

      queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const { mutate: cloneSchedule, isPending: isCloningSchedule } = useMutation({
    mutationFn: async (scheduleId: string) => {
      return await (await ApiService.CloneSchedule(scheduleId)).json();
    },
    onSuccess: () => {
      toast.success('Schedule cloned successfully');
      queryClient.invalidateQueries({
        queryKey: scheduleKeys.all,
      });
      setSelectedRows({});
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to clone schedule');
    },
  });

  const handleArchiveSchedules = () => {
    if (schedulesData?.pages) {
      const toArchive = getSelectedSchedules(schedulesData);
      archiveSchedules(toArchive.map((schedule) => schedule.id));
    }
  };

  const handleRestoreSchedules = () => {
    if (schedulesData?.pages) {
      const toRestore = getSelectedSchedules(schedulesData);
      restoreSchedules(toRestore.map((schedule) => schedule.id));
    }
  };

  const handleAddSchedule = () => {
    navigate('/schedules/new');
  };

  const selectedSchedules = useMemo(() => getSelectedSchedules(schedulesData), [schedulesData, getSelectedSchedules]);

  useEffect(() => {
    if (schedulesError) {
      toast.error(schedulesError.message);
    }
  }, [toast, schedulesError]);

  useResetRowSelectionOnChange(setSelectedRows, [scheduleState, debouncedSearch, filters, sorting.key, sorting.direction, grouping]);

  const { sentinelRef, canLoadMore, loadMore } = useInfiniteScrollTrigger({
    fetchNextPage: fetchNextSchedulesPage,
    hasNextPage: hasNextSchedulesPage,
    isFetching: isFetchingSchedules,
    isError: !!schedulesError,
    root: scrollRoot,
  });

  const flattenedData = useMemo(() => schedulesData?.pages.flatMap((page) => page.data) ?? [], [schedulesData]);

  useEffect(() => {
    if (!view) {
      updatePageConfig({
        view: 'grid',
      });
    }
  }, [view, updatePageConfig]);

  useEffect(() => {
    syncModuleColumns(MODULE.Schedules, createSchedulesTableColumns());
  }, [syncModuleColumns]);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <TablePageHeader>
        <div className="no-scrollbar h-full w-full overflow-x-auto py-0.5">
          <div className="flex h-full min-w-[580px] items-center justify-between">
            {!selectedSchedules.length && (
              <>
                <div className="flex h-full items-center gap-x-2 px-2">
                  <TablePageTitle>Schedules</TablePageTitle>
                  <ButtonStateToggle
                    options={[
                      { icon: <FolderOpenIcon />, label: 'Active', value: RecordState.Active },
                      { icon: <ArchiveIcon />, label: 'Archived', value: RecordState.Archived },
                    ]}
                    value={scheduleState}
                    onChange={setScheduleState}
                    className="font-medium"
                    responsive
                  />
                  <ButtonStateToggle
                    options={[
                      { icon: <GridIcon />, label: 'Grid', value: 'grid' },
                      { icon: <RegisterIcon />, label: 'Register', value: 'register' },
                    ]}
                    value={view}
                    onChange={(view) =>
                      updatePageConfig({
                        view: view as 'grid' | 'register',
                      })
                    }
                    className="font-medium"
                    responsive
                  />
                  <TablePageSearchBar search={search} setSearch={setSearch} />
                  {filters && filters.length > 0 && (
                    <TablePageHeaderActionButton
                      icon={<UndoIcon />}
                      label="Remove all Filters"
                      kind="secondary"
                      onClick={() => setModuleFilters(MODULE.Schedules, [])}
                    />
                  )}
                </div>

                <div className="mr-1 flex h-full items-center">
                  <div className="flex h-full items-center">
                    <TablePageHeaderActionButton
                      onClick={() => setShareUrl(`${env.VITE_FORMS_URL}/${org?.id}?rawRedirect=true`)}
                      icon={<ShareIcon />}
                      label="Share Link"
                    />
                    <TablePageHeaderActionButton
                      onClick={() => window.open(`${env.VITE_FORMS_URL}/${org?.id}?rawRedirect=true`, '_blank')}
                      icon={<LinkViewIcon />}
                      label="Open Mechanic App"
                    />
                  </div>
                  {canManageSchedules && (
                    <TablePageHeaderActionButton variant="success" icon={<PlusIcon />} kind="primary" onClick={handleAddSchedule} label="Create Schedule" />
                  )}
                </div>
              </>
            )}

            {selectedSchedules.length > 0 && canManageSchedules && (
              <div className="flex items-center gap-1 px-2">
                <TablePageSelectedCountIndicator selectedCount={selectedSchedules.length} totalCount={schedulesData?.pages[0]?.totalCount ?? 0} />
                {selectedSchedules.length === 1 && (
                  <>
                    <TablePageHeaderActionButton
                      icon={<PencilIcon />}
                      label="Edit Schedule"
                      onClick={() => navigate(`/schedules/${selectedSchedules[0].id}`)}
                    />
                    <TablePageHeaderActionButton
                      icon={<CloneIcon />}
                      label="Clone Schedule"
                      onClick={() => cloneSchedule(selectedSchedules[0].id)}
                      loading={isCloningSchedule}
                    />
                  </>
                )}
                {scheduleState === RecordState.Active && (
                  <TablePageHeaderActionButton icon={<ArchiveIcon />} label="Archive Selected" onClick={() => setOpenArchiveConfirmation(true)} />
                )}
                {scheduleState === RecordState.Archived && (
                  <>
                    <TablePageHeaderActionButton icon={<UndoIcon />} label="Restore Selected" onClick={() => setOpenRestoreConfirmation(true)} />
                    <TablePageHeaderActionButton
                      icon={<DeleteIcon className="text-default-red" />}
                      label="Delete Selected"
                      onClick={() => setOpenDeleteConfirmation(true)}
                    />
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </TablePageHeader>

      {view === 'register' && (
        <ScrollArea type="always" viewportRef={setScrollRoot} className="relative flex flex-1 flex-col">
          <ScrollBar orientation="horizontal" />
          <SchedulesTable
            data={flattenedData}
            loading={(isFetchingSchedules && !isFetchingNextSchedulesPage) || isLoadingPageConfig}
            notFoundDisplay={
              debouncedSearch.length > 0 ? (
                <NoSchedulesFoundFromSearchDisplay />
              ) : filters && filters.length > 0 ? (
                <NoSchedulesFoundFromFiltersDisplay removeColumnFilters={() => setModuleFilters(MODULE.Schedules, [])} />
              ) : (
                <NoSchedulesCreatedDisplay />
              )
            }
            rowSelection={selectedRows}
            onRowSelectionChange={setSelectedRows}
            columnFilters={filters || []}
            onColumnFiltersChange={(filters) => setModuleFilters(MODULE.Schedules, filters)}
            sorting={sorting}
            grouping={grouping}
            columnOrder={columnOrder}
            columnVisibility={columnVisibility}
            onSortingChange={(newSorting) =>
              updatePageConfig({
                table: { sorting: newSorting },
              })
            }
          />
          {hasNextSchedulesPage && (
            <Button ref={sentinelRef} onClick={loadMore} disabled={!canLoadMore} className="mx-auto w-full text-center" variant="ghost">
              {isFetchingNextSchedulesPage ? 'Loading more...' : 'Load Newer'}
            </Button>
          )}
        </ScrollArea>
      )}

      {view === 'grid' && (
        <div ref={setScrollRoot} className="scrollbar scrollbar-track-transparent scrollbar-thumb-grey-5 overflow-y-auto">
          <TablePageGridView<GetSchedulesResponseDTO['data'][number]>
            data={flattenedData}
            loading={(isFetchingSchedules && !isFetchingNextSchedulesPage) || isLoadingPageConfig}
            notFoundDisplay={<NoSchedulesCreatedDisplay />}
            ItemComponent={({ item, handleSelectedItemsChange, index }) => (
              <div className="flex flex-col gap-2 p-2">
                <div className="flex w-full justify-between">
                  <ClockIcon />
                  {canManageSchedules && (
                    <label className="-m-2 cursor-pointer p-2" onClick={(e) => e.stopPropagation()}>
                      <Checkbox checked={selectedRows[index]} onCheckedChange={() => handleSelectedItemsChange(index)} />
                    </label>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <div>
                    <div className="line-clamp-2 min-h-[2lh] text-sm font-[700] break-words">{item.name}</div>
                    <div className="text-secondary-text text-xs">{EntityRefUtil.createScheduleRef(item.number)}</div>
                  </div>

                  <div>
                    <div className="text-secondary-text text-xs">
                      <div>Assets: {item.assetCount}</div>
                    </div>
                    <div className="text-secondary-text text-xs">
                      <div>Checks: {item.checkCount}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            selectedItems={selectedRows}
            onSelectedItemsChange={setSelectedRows}
            onItemClick={(item) => {
              // /schedules/:id is the editor — Members can't edit.
              if (!canManageSchedules) return;
              navigate(`/schedules/${item.id}`);
            }}
            cornerFold={false}
            itemClassName="w-[240px]"
          />
          {hasNextSchedulesPage && (
            <Button ref={sentinelRef} onClick={loadMore} disabled={!canLoadMore} className="mx-auto w-full text-center" variant="ghost">
              {isFetchingNextSchedulesPage ? 'Loading more...' : 'Load Newer'}
            </Button>
          )}
        </div>
      )}

      <TypeDeleteConfirmationDialog
        open={openDeleteConfirmation}
        onOpenChange={setOpenDeleteConfirmation}
        callback={handleDeleteSchedules}
        loading={isDeletingSchedules}
        title="Delete Schedules?"
        description="Deleting will permanently remove this schedule and all associated schedule checks."
        note="NOTE: This action is irreversible and will permanently delete all checks created from this schedule."
      />

      <ArchiveConfirmationDialog
        open={openArchiveConfirmation}
        onOpenChange={setOpenArchiveConfirmation}
        callback={handleArchiveSchedules}
        loading={isArchivingSchedules}
        title="Archive schedules?"
        description="Archiving removes these schedules from all deployment rules. Assets will lose their positions and open checks will be deleted. This cannot be undone."
      />

      <RestoreConfirmationDialog
        open={openRestoreConfirmation}
        onOpenChange={setOpenRestoreConfirmation}
        callback={handleRestoreSchedules}
        loading={isRestoringSchedules}
        title="Restore schedules?"
        description="Are you sure you want to restore these schedules? They will be moved back to your active schedules."
      />

      {shareUrl && <ShareDialog url={shareUrl} onOpenChange={() => setShareUrl(null)} open={!!shareUrl} />}

      <FeedbackPrompt {...feedbackPromptProps} />
    </div>
  );
};

export default SchedulesPage;
